import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import some from 'lodash/some';
import isInteger from 'lodash/isInteger';
import isEmpty from 'lodash/isEmpty';
import add from 'lodash/add';
import memoize from 'lodash/memoize';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import forIn from 'lodash/forIn';
import indexOf from 'lodash/indexOf';
import values from 'lodash/values';
import get from 'lodash/get';
import round from 'lodash/round';
import { getPriceFormatWithCommas } from 'yoda-site-components/lib/helpers/Pricing/Pricing';
import { makeInitProductOption } from './BundleProductOptionsSelector';
import { isPromotionalBundle } from './BundleCommonSelector';

const priceFallback = {
    defaults: '$0',
    defaultsMax: '$0',
    defaultsMin: '$0',
};

const getPrice = (state) => state.bundleInventory;
const getQuantity = (state) => state.bundleQuantity;
const getNetPrice = (state) => state.bundleNetPriceDetails;
const getFeatureFlags = (state) => state.context && state.context.featureFlags;

export function getCouponInfo(priceData = {}, isBundleLevel = false) {
    if (
        isBundleLevel ||
        (priceData.couponInfo && priceData.couponInfo.length) ||
        (priceData.eligibleCouponInfo && priceData.eligibleCouponInfo.length)
    ) {
        return get(priceData, 'eligibleCouponInfo[0]') || get(priceData, 'couponInfo[0]', null);
    }

    if (priceData[0]) {
        return (
            get(priceData[0], 'lotPrice.data[0].eligibleCouponInfo[0]') ||
            get(priceData[0], 'lotPrice.data[0].couponInfo[0]')
        );
    }

    return (
        get(priceData, 'lotPrice.data[0].eligibleCouponInfo[0]') ||
        get(priceData, 'lotPrice.data[0].couponInfo[0]', null)
    );
}

export const getProductPrice = (state) => {
    let lotDetails;
    let isProductInStock = true;
    let isItemInStock = true;
    let filterItems = 0;
    let totalCount = 0;
    const productPrice = {};
    let selectedLotPrice;
    const featureFlags = get(state, 'context.featureFlags', {});
    if (
        state.bundleInventory &&
        state.bundleInventory[0] &&
        state.bundleInventory[0].productsPrice
    ) {
        const getInitProductOption = makeInitProductOption();
        const changeFilter = state.productOptions;

        forEach(state.productDetails.products, (products, index) => {
            const selectedProducts = getInitProductOption(state, index);
            const { id } = products.product;
            const selectedOption = filter(changeFilter, (data) => data.productId === id);
            const lotPrice = filter(
                state.bundleInventory[0].productsPrice,
                (price) => price.id === id
            );
            const bundleItemOffering = state.bundleItemOffering[id] || {};
            isProductInStock = selectedProducts[id].isProductInStock;
            isItemInStock = selectedProducts[id].isItemInStock;
            filterItems =
                selectedProducts[id] && selectedProducts[id].filteredItems.length === 1 ? 1 : 0;
            totalCount = add(totalCount, filterItems);
            const getFilterItems = get(selectedProducts[id], 'filteredItems', []);
            let selectedLot;
            /* istanbul ignore next */
            if (Object.keys(changeFilter).length && selectedOption.length && isItemInStock) {
                lotDetails = selectedProducts[id].selectedProductLot;
                selectedLot =
                    lotPrice.length &&
                    filter(lotPrice, (price) =>
                        find(price.lotPrice.data, {
                            id: get(lotDetails, [0, 'id'], ''),
                        })
                    )[0];
                if (
                    selectedLot &&
                    !selectedLot.promotions &&
                    bundleItemOffering &&
                    bundleItemOffering.pricing &&
                    getFilterItems.length === 1
                ) {
                    selectedLotPrice = {
                        ...bundleItemOffering.pricing,
                        isProductInStock,
                        isItemInStock,
                        totalCount,
                    };
                } else {
                    selectedLotPrice = {
                        ...selectedLot,
                        isProductInStock,
                        isItemInStock,
                        totalCount,
                    };
                    if (!selectedLotPrice) {
                        selectedLotPrice = {
                            ...lotPrice[0],
                            isProductInStock,
                            isItemInStock,
                            totalCount,
                        };
                    }
                }
            } else {
                selectedLotPrice = {
                    ...lotPrice[0],
                    isProductInStock,
                    isItemInStock,
                    totalCount,
                    ...(bundleItemOffering.pricing || {}),
                };
                selectedLot = filter(state.productDetails.lots, { isDefault: true });
            }

            const selectedLotDetails =
                selectedLot && lotPrice && selectedLot[0] && lotPrice[0] && lotPrice[0].lotPrice
                    ? lotPrice[0].lotPrice.data[0]
                    : null;

            if (selectedLotDetails && selectedLotDetails.promotions) {
                selectedLotPrice.promotions = selectedLotDetails.promotions;
            }
            productPrice[id] = selectedLotPrice;
        });
    }
    return productPrice;
};

export const getPriceFormat = (min, max, isCurrency) => {
    let priceMatch;
    const dollarSymbol = '$';
    const percentageOff = '% off';
    /* istanbul ignore next */
    if (isCurrency) {
        priceMatch = getPriceFormatWithCommas(
            min === max ? `${dollarSymbol}${max}` : `${dollarSymbol}${min} - ${dollarSymbol}${max}`
        );
    } else {
        priceMatch = min === max ? `${min}${percentageOff}` : `${min} - ${max}${percentageOff}`;
    }
    return priceMatch;
};

const getCalculatePrice = memoize(
    (
        priceData = {},
        isBundleLevel = false,
        quantity,
        isAnyProductOOS,
        isMapPrice,
        isPromotionAvailable
    ) => {
        try {
            let prices = priceData;
            let defaultPrice;
            /* istanbul ignore next */
            if (priceData && priceData[0]) {
                prices = priceData[0];
            }
            const findZeroIndex = indexOf(values(quantity), 0);
            const originalPrice =
                prices.amounts &&
                filter(prices.amounts, (amount) => amount.type.toUpperCase() === 'ORIGINAL')[0];
            /* istanbul ignore next */
            if (
                prices.promotions &&
                prices.promotions.length &&
                findZeroIndex === -1 &&
                !isAnyProductOOS
            ) {
                defaultPrice = prices.promotions[0];
            } else {
                defaultPrice = filter(
                    prices.amounts,
                    (amount) => amount && amount.type.toUpperCase() !== 'ORIGINAL'
                )[0];
                if (
                    defaultPrice &&
                    defaultPrice.type &&
                    (defaultPrice.type.toUpperCase() === 'DEFAULT' ||
                        defaultPrice.type.toUpperCase() === 'BUNDLE')
                ) {
                    defaultPrice = originalPrice;
                }
            }
            let defaults = null;
            let type = null;
            let percentOff = null;
            let original = '';
            let originalType = '';
            let dashed = '';
            let defaultsMin = '';
            let defaultsMax = '';
            let originalMin = '';
            let originalMax = '';
            let promotionalMessage = '';
            let maxSavings = null;
            let mapPrice = '';
            /* istanbul ignore next */
            let originalPriceMin = originalPrice ? originalPrice.min : 0;
            /* istanbul ignore next */
            let originalPriceMax = originalPrice ? originalPrice.max : 0;
            const { isProductInStock } = priceData;
            const { isItemInStock } = priceData;
            const promotions =
                get(prices, 'promotions[0].message', '') ||
                get(prices, 'lotPrice.data[0].promotions[0].message', '');
            const marketingLabel = prices.marketingLabel
                ? prices.marketingLabel
                : get(prices, 'lotPrice.data[0].marketingLabel', '');
            const couponInfo = getCouponInfo(priceData, isBundleLevel);
            /* istanbul ignore next */
            const surcharges =
                prices.surcharges && prices.surcharges[0].amount
                    ? prices.surcharges[0].amount.toFixed(2)
                    : '';
            /* istanbul ignore next */
            if (!prices.manufacturerAdvertised && defaultPrice !== undefined) {
                let defaultPriceMin = defaultPrice ? defaultPrice.min : 0;
                let defaultPriceMax = defaultPrice ? defaultPrice.max : 0;
                /* istanbul ignore next */
                if (!isInteger(defaultPriceMin) || !isInteger(defaultPriceMax)) {
                    defaultPriceMin = defaultPriceMin ? defaultPriceMin.toFixed(2) : 0;
                    defaultPriceMax = defaultPriceMax ? defaultPriceMax.toFixed(2) : 0;
                }
                if (!isInteger(originalPriceMin) || !isInteger(originalPriceMax)) {
                    originalPriceMin = originalPriceMin ? originalPriceMin.toFixed(2) : 0;
                    originalPriceMax = originalPriceMax ? originalPriceMax.toFixed(2) : 0;
                }
                defaults = getPriceFormat(defaultPriceMin, defaultPriceMax, true);
                type =
                    defaultPrice.type &&
                    defaultPrice.type.toUpperCase() !== 'DEFAULT' &&
                    defaultPrice.type.toUpperCase() !== 'BUNDLE' &&
                    defaultPrice.type.toUpperCase() !== 'ORIGINAL'
                        ? defaultPrice.type.toLowerCase()
                        : '';
                percentOff =
                    defaultPrice.minPercentOff > 0
                        ? getPriceFormat(
                              defaultPrice.minPercentOff,
                              defaultPrice.maxPercentOff,
                              false
                          )
                        : '';
                /* istanbul ignore next */
                if (originalPrice !== undefined) {
                    const originalPriceFormat = getPriceFormat(
                        originalPriceMin,
                        originalPriceMax,
                        true
                    );
                    if (
                        defaults !== originalPriceFormat ||
                        (prices.promotions && prices.promotions.length)
                    ) {
                        original = originalPriceFormat;
                        originalType = 'was';
                        dashed = percentOff !== '' ? ' | ' : '';
                    }
                }
                defaultsMin = defaultPriceMin;
                defaultsMax = defaultPriceMax;
                originalMin = originalPriceMin;
                originalMax = originalPriceMax;
            } else {
                /* istanbul ignore next */
                if (!isInteger(originalPriceMin) || !isInteger(originalPriceMax)) {
                    originalPriceMin = originalPriceMin.toFixed(2);
                    originalPriceMax = originalPriceMax.toFixed(2);
                }
                defaults = getPriceFormat(originalPriceMin, originalPriceMax, true);
                originalMin = originalPriceMin;
                originalMax = originalPriceMax;
                if (prices.manufacturerAdvertised) {
                    mapPrice = 'View Price in Cart';
                    originalType = 'was';
                    original = defaults;
                }
            }

            if (isPromotionAvailable) {
                promotionalMessage = 'Save more when you buy all items in the package!';
            }
            if (isPromotionAvailable && findZeroIndex === -1 && !isAnyProductOOS) {
                type = 'package deal';
            }

            if (isBundleLevel && !isAnyProductOOS && findZeroIndex === -1) {
                maxSavings = prices.maxSavings ? `Up to $${prices.maxSavings} in savings` : '';
            }

            if (isBundleLevel && isMapPrice) {
                mapPrice = 'View Price in Cart';
            }

            const price = {
                defaults,
                original,
                originalType,
                percentOff,
                type,
                marketingLabel,
                promotions,
                dashed,
                surcharges,
                defaultsMin,
                defaultsMax,
                originalMin,
                originalMax,
                isProductInStock,
                isItemInStock,
                promotionalMessage,
                maxSavings,
                mapPrice,
                couponInfo,
                priceLabel: priceData.priceLabel || '',
                isBundleLevelPrice: isBundleLevel,
                couponExclusionMessage: get(
                    priceData,
                    'lotPrice.data[0].couponExclusionMessage',
                    ''
                ),
            };
            return price;
        } catch (err) {
            console.log(err);
            return priceFallback;
        }
    }
);

/* istanbul ignore next */
const getBundleNetPrice = (pricingDetails, { totals = {} }) => {
    const netTotal = get(totals, 'netTotal', '');
    const promoTotal = get(totals, 'netTotalWithCoupon', 0);

    const wasPrice = get(totals, 'originalPrice', 0);
    const productLabel = get(totals, 'rollUpPriceType', '');

    if (netTotal) {
        pricingDetails.defaults = `$${netTotal}`;
        pricingDetails.priceLabel = '';
    }

    if (wasPrice > 0) {
        pricingDetails.original = `$${wasPrice}`;
    }

    if (productLabel) {
        pricingDetails.type = productLabel.toLowerCase();
    }

    if (pricingDetails.couponInfo) {
        const eligibilityMessage = get(totals, 'eligibilityMessage', '');

        pricingDetails.couponInfo.eligibilityMessage = eligibilityMessage;
    }

    pricingDetails.isNetLevelPrice = true;
    pricingDetails.promoTotal = promoTotal;

    return pricingDetails;
};

const getBundleLevelPrice = (
    productPrice,
    bundlePrice,
    quantity,
    initialPageLoad,
    isPromotionAvailable
) => {
    let bundlePriceData = {};
    const isAllProductOOS = some(productPrice, { isProductInStock: true });
    const isAnyProductOOS = some(productPrice, { isItemInStock: false });
    const isAnyProductInstock = some(productPrice, { isItemInStock: true });
    const isMapPrice = some(productPrice, { mapPrice: 'View Price in Cart' });
    const zeroQuantityCount = filter(values(quantity), (data) => data === 0);
    /* istanbul ignore next */
    if (
        initialPageLoad ||
        !isAllProductOOS ||
        !isAnyProductInstock ||
        zeroQuantityCount.length === Object.keys(productPrice).length
    ) {
        bundlePriceData = getCalculatePrice(
            bundlePrice,
            true,
            quantity,
            isAnyProductOOS,
            isMapPrice,
            isPromotionAvailable
        );
    } else {
        let defaultMaxPrice = 0;
        let defaultMinPrice = 0;
        let originalMinPrice = 0;
        let originalMaxPrice = 0;
        let maxPercentOff = 0;
        let minPercentOff = 0;
        let isZeroQuantityAvailable = false;
        let maxSavings = '';
        let originalType = '';
        let dashed = '';
        let promotionalMessage = '';
        let mapPrice = '';
        const isAnyItemOOS = some(productPrice, { isItemInStock: false });
        const findZeroIndex = indexOf(values(quantity), 0);
        const marketingLabel = bundlePrice.marketingLabel ? bundlePrice.marketingLabel : '';
        const defaultPrice = filter(
            bundlePrice.amounts,
            (amount) => amount.type.toUpperCase() !== 'ORIGINAL'
        )[0];
        let type = '';
        if (
            defaultPrice &&
            defaultPrice.type &&
            defaultPrice.type.toUpperCase() !== 'DEFAULT' &&
            defaultPrice.type.toUpperCase() !== 'BUNDLE'
        ) {
            type = defaultPrice.type.toLowerCase();
        }
        forIn(productPrice, (price, key) => {
            if (quantity[key] === 0) {
                isZeroQuantityAvailable = true;
            }
            if (price.isProductInStock && price.isItemInStock && !(quantity[key] === 0)) {
                originalMinPrice = add(originalMinPrice, Number(price.originalMin));
                originalMaxPrice = add(originalMaxPrice, Number(price.originalMax));
                /* istanbul ignore next */
                if (price.defaultsMax !== '' || price.defaultsMin !== '') {
                    defaultMaxPrice = add(defaultMaxPrice, Number(price.defaultsMax));
                    defaultMinPrice = add(defaultMinPrice, Number(price.defaultsMin));
                } else {
                    defaultMaxPrice = add(defaultMaxPrice, Number(price.originalMax));
                    defaultMinPrice = add(defaultMinPrice, Number(price.originalMin));
                }
                maxPercentOff = round(
                    (((originalMaxPrice - defaultMaxPrice) / originalMaxPrice) * 100).toFixed(2)
                );
                minPercentOff = round(
                    (((originalMinPrice - defaultMinPrice) / originalMinPrice) * 100).toFixed(2)
                );
            }
        });
        let original = getPriceFormat(
            originalMinPrice.toFixed(2),
            originalMaxPrice.toFixed(2),
            true
        );
        let defaults = getPriceFormat(defaultMinPrice.toFixed(2), defaultMaxPrice.toFixed(2), true);
        const percentOff =
            minPercentOff > 0 ? getPriceFormat(minPercentOff, maxPercentOff, false) : '';
        if (defaults !== original) {
            originalType = 'was';
            dashed = percentOff !== '' ? ' | ' : '';
        } else {
            original = '';
        }
        /* istanbul ignore next */
        if (defaultMinPrice === 0) {
            defaults = getPriceFormat(originalMinPrice, originalMaxPrice, true);
            original = '';
        }
        /* istanbul ignore next */
        if (!isZeroQuantityAvailable && !isAnyItemOOS) {
            const bundleSavings = round(originalMinPrice - defaultMinPrice);
            maxSavings = bundlePrice.maxSavings ? `Up to $${bundleSavings} in savings` : '';
        }
        if (isPromotionAvailable && findZeroIndex === -1 && !isAnyProductOOS && !isAnyItemOOS) {
            type = 'package deal';
        }
        if (isPromotionAvailable) {
            promotionalMessage = 'Save more when you buy all items in the package!';
        }
        if (isMapPrice) {
            mapPrice = 'View Price in Cart';
        }
        bundlePriceData = {
            original,
            defaults,
            percentOff,
            marketingLabel,
            type,
            maxSavings,
            originalType,
            dashed,
            promotionalMessage,
            mapPrice,
            priceLabel: bundlePrice.priceLabel,
            couponInfo: bundlePrice ? bundlePrice.couponInfo[0] : null,
            isBundleLevelPrice: true,
        };
    }

    return bundlePriceData;
};

export const bundleLotsPricingSelector = createSelector(
    [getPrice, getQuantity, isPromotionalBundle],
    (price, quantity, isPromotionAvailable) => {
        const { productsPrice } = price[0];
        const isAnyProductOOS = some(productsPrice, { isItemInStock: false });
        const allLotsPrice = [];
        forIn(productsPrice, (productPrice, index) => {
            const lotPriceDetail = getCalculatePrice(
                productPrice,
                false,
                quantity,
                isAnyProductOOS,
                false,
                isPromotionAvailable
            );
            allLotsPrice.push({
                lotId: productPrice.lotPrice.data[0].id,
                ...lotPriceDetail,
            });
        });
        return allLotsPrice;
    }
);

const bundlePricingSelector = createSelector(
    [getNetPrice, getPrice, getProductPrice, getQuantity, getFeatureFlags, isPromotionalBundle],
    (netPrice, price, productPrice, quantity, featureFlags, isPromotionAvailable) => {
        let calcChildLevelPrice = {};
        let calcBundleLevelPrice = {};
        let calcBundleNetPrice = { showBogo: true };
        /* istanbul ignore next */
        if (Object.keys(productPrice).length) {
            const isAnyProductOOS = some(productPrice, { isItemInStock: false });
            forIn(productPrice, (amounts, index) => {
                calcChildLevelPrice[index] = getCalculatePrice(
                    amounts,
                    false,
                    quantity,
                    isAnyProductOOS,
                    false,
                    isPromotionAvailable
                );
            });
            calcBundleLevelPrice = getBundleLevelPrice(
                calcChildLevelPrice,
                price[0].bundleprice,
                quantity,
                true,
                isPromotionAvailable
            );
            if (netPrice && netPrice.lineItems && netPrice.lineItems.length) {
                calcBundleNetPrice = getBundleNetPrice({ ...calcBundleLevelPrice }, netPrice);
            } else {
                calcBundleNetPrice = calcBundleLevelPrice;
            }
            if (featureFlags.enableNewBogoLogic) {
                forEach(productPrice, (ppPrice, ppId) => {
                    if (isEmpty(ppPrice.promotions) && quantity[ppId] > 0) {
                        const defaultPrice = ppPrice.amounts.filter((amount) =>
                            ['CLEARANCE', 'DEFAULT', 'SALE'].indexOf(amount.type.toUpperCase() > -1)
                        );
                        calcBundleNetPrice.showBogo = defaultPrice.length === 0;
                    }
                });
            }
        } else if (price[0] && !price[0].bundleprice) {
            calcChildLevelPrice = { price: 'oops something went wrong' };
            calcBundleLevelPrice = { price: 'oops something went wrong' };
        }

        return {
            calcChildLevelPrice,
            calcBundleLevelPrice,
            calcBundleNetPrice,
        };
    }
);

export const getProductsCoupons = createSelector([getPrice], (bundleInventory = []) => {
    if (bundleInventory.length && bundleInventory[0] && bundleInventory[0].productsPrice) {
        const productsCoupons = bundleInventory[0].productsPrice.map(
            (product) =>
                get(product, 'lotPrice.data[0].couponInfo[0]') ||
                get(product, 'lotPrice.data[0].eligibleCouponInfo[0]', null)
        );
        return productsCoupons;
    }

    return [];
});

export const isSameProductsCoupons = createSelector(
    [getProductsCoupons],
    (coupons = []) =>
        !!(
            coupons.length &&
            coupons.every((coupon) => coupon && coupon.alphaId === coupons[0].alphaId)
        )
);

export const isAllItemsHaveCoupons = createSelector(
    [getProductsCoupons, getPrice],
    (coupons = [], bundleInventory = []) =>
        !!(
            coupons.length &&
            bundleInventory.length &&
            coupons.length === bundleInventory[0].productsPrice.length
        )
);

export default bundlePricingSelector;
